import classNames from 'classnames'

import Button, {
  ButtonTypes,
  ButtonVariants,
} from '~/components/atoms/Button/Button'
import { Icon } from '~/components/atoms/Icon/Icon'
import Image from '~/components/atoms/Image/Image'
import Link from '~/components/atoms/Link/Link'
import Text, { TextSizes } from '~/components/atoms/Text/Text'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import useGeoLocation from '~/hooks/useLocation'
import { currencyCodeToSymbolMap } from '~/utils/currencyMappers'
import { numberWithCommas } from '~/utils/formatCurrency'
import { toURL } from '~/utils/urlResolver'

import GrowthStatus from '../GrowthStatus/GrowthStatus'

import styles from './MAB100CollectionCard.module.css'

interface MAB100CollectionCardHeadProps {
  title: string
  signed: string
  artist?: string
}

export const MAB100CollectionCardHead = ({
  title,
  signed,
  artist,
}: MAB100CollectionCardHeadProps) => {
  return (
    <div className={styles.text}>
      {title && (
        <Text
          Tag="p"
          className={styles.title}
          size={TextSizes.secondaryTitleMed}
        >
          {title}
        </Text>
      )}
      {signed && (
        <Text
          Tag="p"
          className={styles.signedItem}
          size={TextSizes.secondaryTitleSml}
        >
          {signed}
        </Text>
      )}
      {artist && (
        <Text Tag="p" size={TextSizes.primaryTitleSml}>
          {artist}
        </Text>
      )}
    </div>
  )
}

export interface MAB100CollectionCardItemProps {
  artist: string
  artistSlug: string
  artworkId: string
  artworkSlug: string
  collectionSlug: any
  collectionTaxonomy: any
  format: string
  heroProps: any
  image: any
  listPriceMax: number
  listPriceMaxValues: any
  listPriceMin: number
  listPriceMinValues: any
  poa: boolean
  signed: string
  title: string
  _id: string
  aagr: number
  sellTicketsCount: number
  ownedTicketsCount: number
  buyTicketsCount: number
  rank: number
}

export interface MAB100CollectionCardProps {
  testID?: string
  item: MAB100CollectionCardItemProps
  offwhite?: boolean
  rank: number
  openForm: (
    supplyProps: FormTypes,
    item: MAB100CollectionCardItemProps
  ) => void
}

export default function MAB100CollectionCard({
  item,
  offwhite = false,
  rank,
  testID,
  openForm,
}: MAB100CollectionCardProps) {
  if (!item) return null

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { activeCurrencyIso } = useGeoLocation()

  const currentCurrency = activeCurrencyIso
    ? currencyCodeToSymbolMap[activeCurrencyIso]
    : '£'

  const { image, title, signed, artist, format } = item

  const renderTradingStatus = (item: MAB100CollectionCardItemProps) => {
    const buyable = item.sellTicketsCount > 1

    const sellable = item.buyTicketsCount > 0

    const sellCol = () => (
      <div className={styles.headerAction}>
        <Text uppercase className={styles.headerActionTitle}>
          Wanted
        </Text>
        <Button
          variant={ButtonVariants.primaryDark}
          reducedPadding={true}
          smallFont={true}
          iconLeft={<Icon type={'Arrow'} />}
          button={{
            type: ButtonTypes.button,
            onClick: () => openForm(FormTypes.sell, item),
          }}
        >
          Enquire to sell
        </Button>
      </div>
    )

    if (sellable) sellCol()

    if (buyable) {
      return (
        <div className={styles.headerAction}>
          <Text uppercase className={styles.headerActionTitle}>
            In Network
          </Text>
          <Button
            variant={ButtonVariants.primaryDark}
            reducedPadding={true}
            smallFont={true}
            iconLeft={<Icon type={'Arrow'} />}
            button={{
              type: ButtonTypes.button,
              onClick: () => openForm(FormTypes.buy, item),
            }}
          >
            Enquire to buy
          </Button>
        </div>
      )
    }

    return sellCol()
  }

  return (
    <div
      className={classNames(styles.base, offwhite && styles.offwhite)}
      data-testid={testID}
    >
      <div className={styles.cardContainer}>
        <div className={styles.cardRank}>
          <Text>{rank}</Text>
        </div>
        <div className={styles.cardDetails}>
          {renderTradingStatus(item)}
          <Link
            href={toURL({
              slug: item.artworkSlug,
              artist: item.artistSlug,
              collection: item.collectionSlug,
              collectionTaxonomy: item.collectionTaxonomy,
              _type: 'artwork',
            })}
          >
            <div className={styles.headerWrap}>
              <div className={styles.imageCol}>
                <div className={styles.imageContainer}>
                  <div className={classNames(styles.imageContainerInner)}>
                    {image && image.asset && (
                      <Image
                        asset={image.asset}
                        alt={`${title} - ${format}`}
                        objectFit="contain"
                        // onClick={() =>
                        //   item.hasHistory && openInfoWindow(item, variant)
                        // }
                        sizes="(max-width: 559px) 100vw, (max-width: 1079px) 33vw, 25vw"
                      />
                    )}
                  </div>
                </div>
              </div>
              <div className={styles.textCol}>
                <div className={styles.text}>
                  {title && (
                    <Text
                      Tag="p"
                      className={styles.title}
                      size={TextSizes.secondaryTitleMed}
                    >
                      {title}
                    </Text>
                  )}
                  {signed && (
                    <Text
                      Tag="p"
                      className={styles.signedItem}
                      size={TextSizes.secondaryTitleSml}
                    >
                      {signed}
                    </Text>
                  )}
                  {artist && (
                    <Text Tag="p" size={TextSizes.primaryTitleSml}>
                      {artist}
                    </Text>
                  )}
                  <div className={styles.bottom}>
                    <div className={styles.guidePriceContainer}>
                      <Text className={styles.guidePriceAmount}>
                        {currentCurrency}
                        {numberWithCommas(item.listPriceMin)}&nbsp;-&nbsp;
                        {currentCurrency}
                        {numberWithCommas(item.listPriceMax)}
                      </Text>
                      <Text
                        size={TextSizes.secondaryTitleMicro}
                        uppercase
                        className={styles.guidePriceEst}
                      >
                        EST.
                      </Text>
                    </div>
                    {item?.aagr && (
                      <GrowthStatus graphGrey growth={+item.aagr} />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </Link>
        </div>
      </div>
    </div>
  )
}
